import { Modal } from "@components";
import { useDocumentSignatureContext } from "@contexts";
import {
  parseBlockingTimeToSeconds,
  calculateUnblockTimestamp,
} from "@interfaces";
import { styled } from "@mui/material";
import { useSessionPersistentCountdown } from "@hooks";
import { useEffect } from "react";

const BoldText = styled("span")({
  fontWeight: 600,
});

interface Props {
  onReEnlistment: () => void;
  isUnenrolling?: boolean;
  isStartingEsignFlow?: boolean;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
}

export function BlockedPinDocumentSignatureErrorModal({
  onReEnlistment,
  isUnenrolling = false,
  isStartingEsignFlow = false,
  onPrimaryAction,
  onSecondaryAction,
}: Props) {
  const {
    showBlockedPinErrorModal,
    esignErrorMessage,
    setShowInvalidPinErrorModal,
    setShowBlockedPinErrorModal,
  } = useDocumentSignatureContext();

  // Countdown for blocked PIN
  const blockingTimeInSeconds = parseBlockingTimeToSeconds(esignErrorMessage);

  const { formattedTime, start, clear, setUnblockTimestamp } =
    useSessionPersistentCountdown({
      initialTimeInSeconds: blockingTimeInSeconds,
      autoStart: false,
      sessionStorageKey: "pinUnblockTimestamp",
      onComplete: () => {
        setShowBlockedPinErrorModal(false);
        setShowInvalidPinErrorModal(true);
      },
    });

  useEffect(
    function startCountdownWhenModalOpens() {
      if (showBlockedPinErrorModal) {
        clear();

        // Calculate and store the unblock timestamp
        const unblockTimestamp = calculateUnblockTimestamp(esignErrorMessage);
        setUnblockTimestamp(unblockTimestamp);

        start();
      }
    },
    [
      showBlockedPinErrorModal,
      clear,
      start,
      esignErrorMessage,
      setUnblockTimestamp,
    ]
  );

  // Clear countdown when modal closes or on unmount
  useEffect(() => {
    if (!showBlockedPinErrorModal) {
      clear();
    }
    return () => {
      clear();
    };
  }, [showBlockedPinErrorModal, clear]);

  return (
    <Modal
      title="Tu PIN ha sido bloqueado"
      content={
        <>
          <BoldText>Verifica tu identidad</BoldText> para crear uno nuevo o
          espera <BoldText>{formattedTime}</BoldText> para que se desbloquee
        </>
      }
      isOpen={showBlockedPinErrorModal}
      buttonProps={{
        buttonText: "Verificar identidad",
        onClick: () => {
          clear();
          onReEnlistment();
          onPrimaryAction?.();
        },
        loading: isUnenrolling || isStartingEsignFlow,
      }}
      secondaryButtonProps={{
        buttonText: "Volver a inicio",
        onClick: () => {
          clear();
          setShowBlockedPinErrorModal(false);
          onSecondaryAction?.();
        },
      }}
    />
  );
}
